import { Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ANIMATIONS, COURSE_DISCIPLINES, COURSE_FORMAT, COURSE_LANGUAGE, COURSE_TYPES, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { ConfirmationDialogueComponent } from 'app/components/common/confirmation-dialogue/confirmation-dialogue.component';
import { PaymentService } from 'app/components/payments/service/payment.service';
import { IAtp } from 'app/models/atp/atp';
import { IAtpAccreditation } from 'app/models/atp/atp-accreditation';
import { IAtpCourse } from 'app/models/atp/atp-course';
import { IAtpCourseAddress } from 'app/models/atp/atp-course-address';
import { IAtpPerson } from 'app/models/atp/atp-person';
import { IAddress } from 'app/models/common/address';
import { IPerson } from 'app/models/people/person';
import { AtpService } from 'app/services/atp/atp.service';
import { CommonService } from 'app/services/common/common.service';
import { SharedService } from 'app/services/core/shared.service';
import { ToastrService } from 'ngx-toastr';
import { AtpEditCourseStudentComponent } from '../atp-edit-course-student/atp-edit-course-student.component';
import { AtpCourseStudentUploadComponent } from '../atp-course-student-upload/atp-course-student-upload.component';
import { AtpEditCourseDocumentComponent } from '../atp-edit-course-document/atp-edit-course-document.component';
import { forkJoin } from 'rxjs';

interface CourseAddressOption {
  key: string;
  locationName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  label: string;
}

@Component({
  selector: 'app-atp-edit-course',
  templateUrl: './atp-edit-course.component.html',
  styleUrls: ['./atp-edit-course.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: ANIMATIONS
})
export class AtpEditCourseComponent implements OnInit {
  public personList:IPerson[] = [];
  public filteredPersonList: IPerson[] = []; 
  public courseAddressOptions: CourseAddressOption[] = [];
  public filteredCourseAddressOptions: CourseAddressOption[] = [];

  public uiData = LEAD_BASED_PAINT;
  public courseDataForm = null;
  public courseData: IAtpCourse = null;  
  public minimumCourseDate: Date = null;
  private savedWebAddress: string = null; // Stores webAddress when format is not Online/Hybrid

  public courseLocation: IAtpCourseAddress = null;  
  public courseAddress: IAddress = null;  
  
  public tableColumns = ['status', 'studentName', 'courseCertificateNumber','testScore','actions'];
  public tableFormat = [        
    {displayName:'Status',columnName:'status', type:'status', size:'18'},
    {displayName:'Student Name',columnName:'studentName', type:'string', size:'42'},
    {displayName:'Certificate Number',columnName:'courseCertificateNumber', type:'string', size:'25'},
    {displayName:'Test Score',columnName:'testScore', type:'string', size:'15'},
  ];
  public tableData = []
  public tableDataFiltered = []
  public tablePageSize = 5;
  public filterTableString: string = '';  
  public tableDataSource: MatTableDataSource<any>;
  public changeIndicator: number = 0;
  public cardColor: string = this.sharedService.gold + '20';
  public canEdit: boolean = true;
  public validAccreditations: IAtpAccreditation[] = [];
  
  // Mapping of discipline display names to keywords to search for in accreditation endorsement types
  private disciplineKeywords: { [key: string]: string[] } = {
    'Dust Sampling Technician': ['dust'],
    'Inspector': ['inspector'],
    'Project Designer': ['project'],
    'Renovator': ['renovator'],
    'Renovator (Hands On)': ['renovator'],
    'Risk Assessor': ['risk'],
    'Supervisor': ['supervisor'],
    'Worker': ['worker']
  };

  @ViewChild('TablePaginator') tablePaginator: MatPaginator;
  @ViewChild('TableSort') tableSort: MatSort;
  @ViewChild(AtpEditCourseDocumentComponent) courseDocumentComponent: AtpEditCourseDocumentComponent;
  
  constructor(public sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public data:{course:IAtpCourse, atp:IAtp, atpStaff:IAtpPerson [], courseId: number},
    private readonly toastr: ToastrService,
    private readonly atpService: AtpService,
    private readonly commonService: CommonService,
    public paymentService: PaymentService,    
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.calculateMinimumCourseDate();
    this.setCourseDataFormFields();
    this.loadValidAccreditations();
  }

  // Load valid (non-expired) accreditations for the ATP from the service
  loadValidAccreditations(): void {
    if (!this.data.atp?.id) {
      this.validAccreditations = [];
      this.loadData();
      return;
    }
    
    this.atpService.getAtpAccreditations(this.data.atp.id).subscribe(accreditations => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      this.validAccreditations = (accreditations || []).filter(accred => {
        if (!accred.expirationDate) return false;
        const expDate = new Date(accred.expirationDate);
        expDate.setHours(0, 0, 0, 0);
        return expDate >= today;
      });
      
      this.loadData();
    }, error => {
      console.error('Error loading accreditations:', error);
      this.validAccreditations = [];
      this.loadData();
    });
  }

  // Get valid accreditations that match a specific discipline
  getAccreditationsForDiscipline(discipline: string): IAtpAccreditation[] {
    const keywords = this.disciplineKeywords[discipline] || [];
    return this.validAccreditations.filter(accred => {
      const type = accred.accreditationEndorsementType?.toLowerCase() || '';
      return keywords.some(keyword => type.includes(keyword.toLowerCase()));
    });
  }

  // Check if any valid accreditation contains a specific keyword
  hasAccreditationWithKeyword(keyword: string, disciplineAccreditations?: IAtpAccreditation[]): boolean {
    const accreds = disciplineAccreditations || this.validAccreditations;
    return accreds.some(accred => {
      const type = accred.accreditationEndorsementType?.toLowerCase() || '';
      return type.includes(keyword.toLowerCase());
    });
  }

  calculateMinimumCourseDate(): void {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 7); // Add 7 days
    this.minimumCourseDate = minDate;
  }

  /**
   * Checks if we are within 7 days of the course start date.
   * Returns true if the course starts within the next 7 days (meaning editing should be locked).
   */
  isWithin7DaysOfCourseStart(): boolean {
    if (!this.courseData?.courseStart || this.courseData?.id === 0) {
      return false;
    }
    
    const courseStartDate = new Date(this.courseData.courseStart);
    courseStartDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const sevenDaysFromNow = new Date(today);
    sevenDaysFromNow.setDate(today.getDate() + 7);
    
    // Course starts within 7 days (including today or in the past)
    return courseStartDate < sevenDaysFromNow;
  }

  /**
   * Checks if editing should be disabled for this course.
   * Editing is disabled only if post notification has been received.
   * Note: The 7-day rule only affects whether save is allowed, not whether editing is locked.
   */
  isEditingDisabled(): boolean {
    return this.courseData?.receivedPostNote === true;
  }

  /**
   * Checks if the course start date is valid (at least 7 days from today).
   * Used to disable the save button if the date doesn't meet the 7-day requirement.
   * Also checks online lecture start date for Hybrid courses.
   */
  isCourseStartDateValid(): boolean {
    const minDate = new Date(this.minimumCourseDate);
    minDate.setHours(0, 0, 0, 0);
    
    // Check course start date
    const courseStartValue = this.courseDataForm?.get('courseStart')?.value;
    if (courseStartValue) {
      const courseStartDate = new Date(courseStartValue);
      courseStartDate.setHours(0, 0, 0, 0);
      if (courseStartDate < minDate) {
        return false;
      }
    }
    
    // Check online lecture start date for Hybrid courses
    if (this.isHybridFormat()) {
      const onlineLectureStartValue = this.courseDataForm?.get('onlineLectureStart')?.value;
      if (onlineLectureStartValue) {
        const onlineLectureStartDate = new Date(onlineLectureStartValue);
        onlineLectureStartDate.setHours(0, 0, 0, 0);
        if (onlineLectureStartDate < minDate) {
          return false;
        }
      }
    }
    
    return true;
  }

  /**
   * Gets a message explaining why the save button is disabled due to date validation.
   */
  getDateValidationMessage(): string {
    const minDate = new Date(this.minimumCourseDate);
    minDate.setHours(0, 0, 0, 0);
    
    // Check course start date
    const courseStartValue = this.courseDataForm?.get('courseStart')?.value;
    if (courseStartValue) {
      const courseStartDate = new Date(courseStartValue);
      courseStartDate.setHours(0, 0, 0, 0);
      if (courseStartDate < minDate) {
        return 'Course start date must be at least 7 days from today.';
      }
    }
    
    // Check online lecture start date for Hybrid courses
    if (this.isHybridFormat()) {
      const onlineLectureStartValue = this.courseDataForm?.get('onlineLectureStart')?.value;
      if (onlineLectureStartValue) {
        const onlineLectureStartDate = new Date(onlineLectureStartValue);
        onlineLectureStartDate.setHours(0, 0, 0, 0);
        if (onlineLectureStartDate < minDate) {
          return 'Online lecture start date must be at least 7 days from today.';
        }
      }
    }
    
    return '';
  }

  validateCourseStartDate = (control: any): { [key: string]: any } | null => {
    if (!control.value || !this.minimumCourseDate) {
      return null;
    }
    
    const selectedDate = new Date(control.value);
    selectedDate.setHours(0, 0, 0, 0);
    const minDate = new Date(this.minimumCourseDate);
    minDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < minDate) {
      return { 'minDate': { value: control.value, requiredDate: this.minimumCourseDate } };
    }
    
    return null;
  }

  validateCourseEndDate = (control: any): { [key: string]: any } | null => {
    if (!control.value) {
      return null;
    }
    
    const selectedEndDate = new Date(control.value);
    selectedEndDate.setHours(0, 0, 0, 0);
    
    // Check if end date is at least 7 days from today
    if (this.minimumCourseDate) {
      const minDate = new Date(this.minimumCourseDate);
      minDate.setHours(0, 0, 0, 0);
      
      if (selectedEndDate < minDate) {
        return { 'minDate': { value: control.value, requiredDate: this.minimumCourseDate } };
      }
    }
    
    // Check if end date/time is before start date/time
    const startDateControl = this.courseDataForm?.get('courseStart');
    if (startDateControl && startDateControl.value) {
      const startDate = new Date(startDateControl.value);
      const endDate = new Date(control.value);
      
      // Apply times if available
      const startTimeControl = this.courseDataForm?.get('courseStartTime');
      const endTimeControl = this.courseDataForm?.get('courseEndTime');
      
      if (startTimeControl?.value) {
        const [startHours, startMinutes] = startTimeControl.value.split(':').map(Number);
        startDate.setHours(startHours || 0, startMinutes || 0, 0, 0);
      } else {
        startDate.setHours(0, 0, 0, 0);
      }
      
      if (endTimeControl?.value) {
        const [endHours, endMinutes] = endTimeControl.value.split(':').map(Number);
        endDate.setHours(endHours || 0, endMinutes || 0, 0, 0);
      } else {
        endDate.setHours(0, 0, 0, 0);
      }
      
      if (endDate < startDate) {
        return { 'endBeforeStart': { value: control.value, startDate: startDate } };
      }
    }
    
    return null;
  }

  validateOnlineLectureStartDate = (control: any): { [key: string]: any } | null => {
    if (!control.value || !this.minimumCourseDate) {
      return null;
    }
    
    const selectedDate = new Date(control.value);
    selectedDate.setHours(0, 0, 0, 0);
    const minDate = new Date(this.minimumCourseDate);
    minDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < minDate) {
      return { 'minDate': { value: control.value, requiredDate: this.minimumCourseDate } };
    }
    
    return null;
  }

  validateOnlineLectureEndDate = (control: any): { [key: string]: any } | null => {
    if (!control.value) {
      return null;
    }
    
    const selectedEndDate = new Date(control.value);
    selectedEndDate.setHours(0, 0, 0, 0);
    
    // Check if end date is at least 7 days from today
    if (this.minimumCourseDate) {
      const minDate = new Date(this.minimumCourseDate);
      minDate.setHours(0, 0, 0, 0);
      
      if (selectedEndDate < minDate) {
        return { 'minDate': { value: control.value, requiredDate: this.minimumCourseDate } };
      }
    }
    
    // Check if end date/time is before start date/time
    const startDateControl = this.courseDataForm?.get('onlineLectureStart');
    if (startDateControl && startDateControl.value) {
      const startDate = new Date(startDateControl.value);
      const endDate = new Date(control.value);
      
      // Apply times if available
      const startTimeControl = this.courseDataForm?.get('onlineLectureStartTime');
      const endTimeControl = this.courseDataForm?.get('onlineLectureEndTime');
      
      if (startTimeControl?.value) {
        const [startHours, startMinutes] = startTimeControl.value.split(':').map(Number);
        startDate.setHours(startHours || 0, startMinutes || 0, 0, 0);
      } else {
        startDate.setHours(0, 0, 0, 0);
      }
      
      if (endTimeControl?.value) {
        const [endHours, endMinutes] = endTimeControl.value.split(':').map(Number);
        endDate.setHours(endHours || 0, endMinutes || 0, 0, 0);
      } else {
        endDate.setHours(0, 0, 0, 0);
      }
      
      if (endDate < startDate) {
        return { 'endBeforeStart': { value: control.value, startDate: startDate } };
      }
    }
    
    return null;
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  cloneCourse(){
    this.courseData.id = 0;
    this.courseDataForm.controls['courseStart'].setValue(null);
    this.courseDataForm.controls['courseStartTime'].setValue(null);
    this.courseDataForm.controls['courseEnd'].setValue(null);
    this.courseDataForm.controls['courseEndTime'].setValue(null);
    this.courseDataForm.controls['onlineLectureStart'].setValue(null);
    this.courseDataForm.controls['onlineLectureStartTime'].setValue(null);
    this.courseDataForm.controls['onlineLectureEnd'].setValue(null);
    this.courseDataForm.controls['onlineLectureEndTime'].setValue(null);
    this.courseDataForm.controls['cancelled'].setValue(false);
    this.courseDataForm.controls['receivedPreNote'].setValue(false);
    this.courseDataForm.controls['receivedPreNoteDate'].setValue(null);
    this.courseDataForm.controls['receivedPostNote'].setValue(false);
    this.courseDataForm.controls['receivedPostNoteDate'].setValue(null);
    this.courseDataForm.controls['receivedStudentFees'].setValue(false);
    this.courseDataForm.controls['students'].setValue(0);
    this.courseDataForm.controls['controlNumber'].setValue(null);
    this.courseDataForm.controls['overrideDelinquency'].setValue(false);
    this.tableData = [];
    this.tableDataFiltered = [];
    this.courseLocation.id = 0;
    this.courseAddress.id = 0;
  }

  clearAddress(){
    this.courseDataForm.controls['locationName'].setValue(null);
    this.courseDataForm.controls['street'].setValue(null);
    this.courseDataForm.controls['city'].setValue(null);
    this.courseDataForm.controls['state'].setValue(null);
    this.courseDataForm.controls['zip'].setValue(null);
    this.courseDataForm.controls['county'].setValue(null);
  }

  clearInstructor(){
    this.courseDataForm.controls['programInstructor'].setValue(null);
    this.courseDataForm.controls['programInstructorEmail'].setValue(null);
    this.courseDataForm.controls['programInstructorPhone'].setValue(null);
  }

  clearEndDate(){
    this.courseDataForm.controls['courseEnd'].setValue(null);
    this.courseDataForm.controls['courseEndTime'].setValue(null);
  }

  clearStartDate(){    
    this.courseDataForm.controls['courseStart'].setValue(null);
    this.courseDataForm.controls['courseStartTime'].setValue(null);
  }

  clearOnlineLectureEndDate(){
    this.courseDataForm.controls['onlineLectureEnd'].setValue(null);
    this.courseDataForm.controls['onlineLectureEndTime'].setValue(null);
  }

  clearOnlineLectureStartDate(){    
    this.courseDataForm.controls['onlineLectureStart'].setValue(null);
    this.courseDataForm.controls['onlineLectureStartTime'].setValue(null);
  }

  loadData(){
    this.courseLocation = {
      id: 0,
      atpCourse: null,
      address: null,
      locationName: "",
      isCurrentMailing: false,
      isCurrentPhysical: true,
    }

    this.courseAddress = {
      id: 0,
      street: "",
      city: "",
      zip: "",
      contactType: "Work",
      county: "",
      state: "OR",
    }
 
    if ((this.data.courseId !== null && this.data.courseId !== undefined && this.data.courseId !== 0) || this.data.course !== null && this.data.course !== undefined){      
      let id = this.data.courseId || this.data.course.id;
      this.atpService.getATPCourseById(id).subscribe(result=>{        
        this.courseData = result;
        this.loadCourseAddressOptions(this.courseData?.atp?.id || this.data?.atp?.id);
        this.refreshStudentList();
        this.loadInstructors();
        this.loadCourseDataForm();
        this.loadCourseAddress();
      },error=>{
        this.toastr.error("There was an error getting the ATP course: ", error)
      })
    } 
    else{
      this.courseData = {
        id: 0,
        atp: this.data.atp,
        atpCourseAddress: this.data?.course?.atpCourseAddress,
        accreditationDiscipline: null,
        accreditationCourseFormat: null,
        accreditationCourseLanguage: null,
        stage: null,
        programName: null,
        courseStart: this.minimumCourseDate?.toISOString() || null,
        courseEnd: null,
        cancelled: false,
        isPrivate: false,
        receivedAmount: null,
        receivedPreNote: false,
        receivedPreNoteDate: null,
        receivedPostNote: false,
        receivedPostNoteDate: null,
        receivedStudentFees: false,      
        students: 0,
        controlNumber: null,   
        programInstructor: null,
        programInstructorEmail: null,
        programInstructorPhone: null,    
        webAddress: null, 
        overrideDelinquency: false,
        courseFeedback: null,
        onlineLectureStart: this.minimumCourseDate?.toISOString() || null,
        onlineLectureEnd: null
      };
      this.loadCourseAddressOptions(this.courseData?.atp?.id || this.data?.atp?.id);
      this.loadInstructors();
      this.loadCourseDataForm();
      this.loadCourseAddress();
    }    
  }
  
  loadInstructors(){
    this.personList = this.data?.atpStaff?.filter(x=>x.isInstructor || x.isPrimaryInstructor || x.isTrainingManager).map(x => x.person);
    this.filteredPersonList = this.personList;    
  }

  loadCourseDataForm(){
    for (const controlName in this.courseData) {
      if (this.courseDataForm.controls.hasOwnProperty(controlName)) {        
        this.courseDataForm.controls[controlName].setValue(this.courseData[controlName]);          
      }
    }    

    if(this.courseData.courseStart !== null){
      this.courseDataForm.controls['courseStartTime'].setValue(this.courseData.courseStart.split('T')[1]);
    }   
    if(this.courseData.courseEnd !== null){
      this.courseDataForm.controls['courseEndTime'].setValue(this.courseData.courseEnd.split('T')[1]);
    }
    if(this.courseData.onlineLectureStart !== null){
      this.courseDataForm.controls['onlineLectureStartTime'].setValue(this.courseData.onlineLectureStart.split('T')[1]);
    }   
    if(this.courseData.onlineLectureEnd !== null){
      this.courseDataForm.controls['onlineLectureEndTime'].setValue(this.courseData.onlineLectureEnd.split('T')[1]);
    }
    
    // Enable dependent fields if discipline is already set (editing existing course)
    if (this.courseData?.accreditationDiscipline) {
      this.courseDataForm.get('stage')?.enable();
      this.courseDataForm.get('accreditationCourseLanguage')?.enable();
      this.courseDataForm.get('accreditationCourseFormat')?.enable();
      
      // Enable website field if format is Online or Hybrid
      if (this.courseData?.accreditationCourseFormat === COURSE_FORMAT.online || this.courseData?.accreditationCourseFormat === COURSE_FORMAT.hybrid) {
        this.courseDataForm.get('webAddress')?.enable();
      }
      
      // Enable online lecture date fields if format is Hybrid
      if (this.courseData?.accreditationCourseFormat === COURSE_FORMAT.hybrid) {
        this.courseDataForm.get('onlineLectureStart')?.enable();
        this.courseDataForm.get('onlineLectureStartTime')?.enable();
        this.courseDataForm.get('onlineLectureEnd')?.enable();
        this.courseDataForm.get('onlineLectureEndTime')?.enable();
      }
    }
    
    // Disable form fields if course editing is locked
    this.updateFormFieldsEditability();
  }

  /**
   * Updates form fields based on editing restrictions.
   * Editing is disabled if:
   * - We are within 7 days of the course start date, OR
   * - Post notification has been received
   */
  updateFormFieldsEditability(): void {
    if (this.isEditingDisabled()) {
      // Disable all course information fields
      this.courseDataForm.get('accreditationDiscipline')?.disable();
      this.courseDataForm.get('accreditationCourseFormat')?.disable();
      this.courseDataForm.get('accreditationCourseLanguage')?.disable();
      this.courseDataForm.get('stage')?.disable();
      this.courseDataForm.get('courseStart')?.disable();
      this.courseDataForm.get('courseStartTime')?.disable();
      this.courseDataForm.get('courseEnd')?.disable();
      this.courseDataForm.get('courseEndTime')?.disable();
      this.courseDataForm.get('onlineLectureStart')?.disable();
      this.courseDataForm.get('onlineLectureStartTime')?.disable();
      this.courseDataForm.get('onlineLectureEnd')?.disable();
      this.courseDataForm.get('onlineLectureEndTime')?.disable();
      this.courseDataForm.get('isPrivate')?.disable();
      this.courseDataForm.get('locationName')?.disable();
      this.courseDataForm.get('street')?.disable();
      this.courseDataForm.get('city')?.disable();
      this.courseDataForm.get('zip')?.disable();
      this.courseDataForm.get('county')?.disable();
      this.courseDataForm.get('state')?.disable();
      this.courseDataForm.get('programInstructor')?.disable();
      this.courseDataForm.get('programInstructorEmail')?.disable();
      this.courseDataForm.get('programInstructorPhone')?.disable();
      this.courseDataForm.get('webAddress')?.disable();
    }
  }

  loadCourseAddress(){
    if(this.courseData !== null && this.courseData.id !== 0){
      this.atpService.getATPCourseAddresses(this.courseData.id).subscribe(result=>{        
        if(result.length){          
          this.courseLocation = result[0];
          this.courseAddress = this.courseLocation.address

          for (const controlName in this.courseAddress) {
            if (this.courseDataForm.controls.hasOwnProperty(controlName)) {        
              this.courseDataForm.controls[controlName].setValue(this.courseAddress[controlName]);          
            }
          }    

          this.courseDataForm.controls['locationName'].setValue(this.courseLocation.locationName);
        }
      },error=>{
        this.toastr.error("There was an error getting the ATP course address: ", error)
      })
    }    
  }

  private getCourseAddressOptionKey(locationName: string, street: string, city: string, state: string, zip: string, county: string): string {
    return [locationName, street, city, state, zip, county]
      .map(value => (value || '').toLowerCase())
      .join('|');
  }

  private buildCourseAddressOptionLabel(locationName: string, street: string, city: string, state: string, zip: string, county: string): string {
    const locality = [city, state].filter(value => !!value).join(', ');
    const localityLine = [locality, zip].filter(value => !!value).join(' ');
    const addressLine = [street, localityLine].filter(value => !!value).join(' | ');
    const countyLine = county ? `${county} County` : '';
    return [locationName, addressLine, countyLine].filter(value => !!value).join(' - ');
  }

  private mapCourseAddressOptions(addresses: IAtpCourseAddress[]): CourseAddressOption[] {
    const dedupedOptions = new Map<string, CourseAddressOption>();

    (addresses || []).forEach(addressRow => {
      const address = addressRow?.address;
      if (!address) {
        return;
      }

      const locationName = (addressRow.locationName || '').toString().trim();
      const street = (address.street || '').toString().trim();
      const city = (address.city || '').toString().trim();
      const state = (address.state || '').toString().trim();
      const zip = (address.zip || '').toString().trim();
      const county = (address.county || '').toString().trim();

      if (!locationName && !street && !city && !state && !zip && !county) {
        return;
      }

      const key = this.getCourseAddressOptionKey(locationName, street, city, state, zip, county);
      if (dedupedOptions.has(key)) {
        return;
      }

      dedupedOptions.set(key, {
        key,
        locationName,
        street,
        city,
        state,
        zip,
        county,
        label: this.buildCourseAddressOptionLabel(locationName, street, city, state, zip, county)
      });
    });

    return Array.from(dedupedOptions.values())
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  private loadCourseAddressOptions(atpId: number | null | undefined): void {
    if (!atpId) {
      this.courseAddressOptions = [];
      this.filteredCourseAddressOptions = [];
      return;
    }

    this.atpService.getATPCourseAddressesByAtpId(atpId).subscribe({
      next: (result) => {
        this.courseAddressOptions = this.mapCourseAddressOptions(result || []);
        this.filteredCourseAddressOptions = [...this.courseAddressOptions];
      },
      error: (error) => {
        console.error('There was an error loading ATP course address options:', error);
        this.courseAddressOptions = [];
        this.filteredCourseAddressOptions = [];
      }
    });
  }

  private filterCourseAddressOptions(searchValue: string): void {
    const normalizedSearchValue = (searchValue || '').toString().toLowerCase().trim();
    if (!normalizedSearchValue) {
      this.filteredCourseAddressOptions = [...this.courseAddressOptions];
      return;
    }

    this.filteredCourseAddressOptions = this.courseAddressOptions.filter(option => {
      const searchable = `${option.locationName} ${option.street} ${option.city} ${option.state} ${option.zip} ${option.county}`.toLowerCase();
      return searchable.includes(normalizedSearchValue);
    });
  }

  private openCourseAddressPanel(trigger: MatAutocompleteTrigger | null | undefined): void {
    if (!trigger) {
      return;
    }

    setTimeout(() => {
      if (!trigger.panelOpen && this.filteredCourseAddressOptions.length > 0) {
        trigger.openPanel();
      }
    });
  }

  onCourseAddressStreetInput(event: any): void {
    this.filterCourseAddressOptions(event?.target?.value || '');
  }

  onCourseAddressStreetFocus(event: any, trigger: MatAutocompleteTrigger): void {
    this.filterCourseAddressOptions(event?.target?.value || '');
    this.openCourseAddressPanel(trigger);
  }

  onCourseAddressOptionSelectionChange(event: any, option: CourseAddressOption): void {
    if (!event?.isUserInput || !event?.source?.selected) {
      return;
    }
    this.selectCourseAddressOption(option);
  }

  selectCourseAddressOption(option: CourseAddressOption): void {
    if (!option) {
      return;
    }

    this.courseDataForm.patchValue({
      locationName: option.locationName || null,
      street: option.street || null,
      city: option.city || null,
      state: option.state || null,
      zip: option.zip || null,
      county: option.county || null
    });
  }

  getCourseAddressOptionLabel(option: CourseAddressOption): string {
    return option?.label || '';
  }

  setCourseDataFormFields(){       
    this.courseDataForm = new UntypedFormGroup({
      accreditationDiscipline: new UntypedFormControl("", [Validators.required]),
      accreditationCourseFormat: new UntypedFormControl({value: "", disabled: true}),      
      accreditationCourseLanguage: new UntypedFormControl({value: "", disabled: true}),      
      stage: new UntypedFormControl({value: "", disabled: true}),      
      courseStart: new UntypedFormControl(null, [this.validateCourseStartDate]),      
      courseStartTime: new UntypedFormControl(null),    
      courseEnd: new UntypedFormControl(null, [this.validateCourseEndDate]),  
      courseEndTime: new UntypedFormControl(null),  
      onlineLectureStart: new UntypedFormControl({value: null, disabled: true}, [this.validateOnlineLectureStartDate]),      
      onlineLectureStartTime: new UntypedFormControl({value: null, disabled: true}),    
      onlineLectureEnd: new UntypedFormControl({value: null, disabled: true}, [this.validateOnlineLectureEndDate]),  
      onlineLectureEndTime: new UntypedFormControl({value: null, disabled: true}),  
      isPrivate: new UntypedFormControl(false),  
      cancelled: new UntypedFormControl({value: false, disabled: true}),  
      receivedPreNote: new UntypedFormControl(false),  
      receivedPreNoteDate: new UntypedFormControl({value: "", disabled: !this.data?.course?.receivedPreNote}, [Validators.required]),  
      receivedPostNote: new UntypedFormControl(false),  
      receivedPostNoteDate: new UntypedFormControl({value: "", disabled: !this.data?.course?.receivedPreNote}, [Validators.required]),  
      receivedStudentFees: new UntypedFormControl({value: false, disabled: true}),        
      students: new UntypedFormControl({value: "", disabled: true}),  
      controlNumber: new UntypedFormControl({value: "", disabled: true}),        
      locationName: new UntypedFormControl(""), 
      street: new UntypedFormControl(""),
      city: new UntypedFormControl(""),      
      zip: new UntypedFormControl(""),      
      county: new UntypedFormControl(""),      
      state: new UntypedFormControl("OR"),        
      programInstructor: new UntypedFormControl(""), 
      programInstructorEmail: new UntypedFormControl(""), 
      programInstructorPhone: new UntypedFormControl(""), 
      webAddress: new UntypedFormControl({value: "", disabled: true}),          
      overrideDelinquency: new UntypedFormControl(false),   
      courseFeedback: new UntypedFormControl(""),
    });
    
    // Re-validate end date when start date changes
    this.courseDataForm.get('courseStart').valueChanges.subscribe(() => {
      this.courseDataForm.get('courseEnd')?.updateValueAndValidity();
    });
    
    // Re-validate end date when start time changes
    this.courseDataForm.get('courseStartTime').valueChanges.subscribe(() => {
      this.courseDataForm.get('courseEnd')?.updateValueAndValidity();
    });
    
    // Re-validate end date when end time changes
    this.courseDataForm.get('courseEndTime').valueChanges.subscribe(() => {
      this.courseDataForm.get('courseEnd')?.updateValueAndValidity();
    });
    
    // Re-validate online lecture end date when online lecture start date changes
    this.courseDataForm.get('onlineLectureStart').valueChanges.subscribe(() => {
      this.courseDataForm.get('onlineLectureEnd')?.updateValueAndValidity();
    });
    
    // Re-validate online lecture end date when online lecture start time changes
    this.courseDataForm.get('onlineLectureStartTime').valueChanges.subscribe(() => {
      this.courseDataForm.get('onlineLectureEnd')?.updateValueAndValidity();
    });
    
    // Re-validate online lecture end date when online lecture end time changes
    this.courseDataForm.get('onlineLectureEndTime').valueChanges.subscribe(() => {
      this.courseDataForm.get('onlineLectureEnd')?.updateValueAndValidity();
    });
    
    // Subscribe to discipline changes to enable/disable dependent fields
    this.courseDataForm.get('accreditationDiscipline').valueChanges.subscribe((value) => {
      if (value) {
        // Enable dependent fields when discipline is selected
        this.courseDataForm.get('stage')?.enable();
        this.courseDataForm.get('accreditationCourseLanguage')?.enable();
        this.courseDataForm.get('accreditationCourseFormat')?.enable();
        this.onDisciplineChange();
      } else {
        // Disable dependent fields when no discipline is selected
        this.courseDataForm.get('stage')?.disable();
        this.courseDataForm.get('accreditationCourseLanguage')?.disable();
        this.courseDataForm.get('accreditationCourseFormat')?.disable();
        this.courseDataForm.get('webAddress')?.disable();
        
        // Clear dependent field values
        this.courseDataForm.get('stage')?.setValue(null);
        this.courseDataForm.get('accreditationCourseLanguage')?.setValue(null);
        this.courseDataForm.get('accreditationCourseFormat')?.setValue(null);
        this.courseDataForm.get('webAddress')?.setValue(null);
      }
    });
    
    // Subscribe to format changes to enable/disable website field
    this.courseDataForm.get('accreditationCourseFormat').valueChanges.subscribe(() => {
      this.onFormatChange();
    });
  }

  updatePreNoteDateDisabledStatus(event: any){
    const receivedPreNoteDateControl = this.courseDataForm.get('receivedPreNoteDate');

    if (event.checked) {      
      receivedPreNoteDateControl.enable();
      receivedPreNoteDateControl.setValue(new Date().toISOString());
    } else {      
      receivedPreNoteDateControl.disable();
      receivedPreNoteDateControl.setValue(null);
    }
  }

  updatePostNoteDateDisabledStatus(event: any){
    const receivedPostNoteDateControl = this.courseDataForm.get('receivedPostNoteDate');

    if (event.checked) {      
      receivedPostNoteDateControl.enable();
      receivedPostNoteDateControl.setValue(new Date().toISOString());
    } else {      
      receivedPostNoteDateControl.disable();
      receivedPostNoteDateControl.setValue(null);
    }
  }

  updateCourseData(): void {
    let timeExpression = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/;
    for (const controlName in this.courseData) {
      if (this.courseDataForm.controls.hasOwnProperty(controlName)) {
        this.courseData[controlName] = this.courseDataForm.controls[controlName].value;        
      }
    } 

    if(this.courseDataForm.controls['courseStart'].value !== null && this.courseDataForm.controls['courseStartTime'].value !== null && timeExpression.test(this.courseDataForm.controls['courseStartTime'].value)){            
      this.courseData.courseStart = typeof this.courseDataForm.controls['courseStart'].value === 'string' ? `${this.courseDataForm.controls['courseStart'].value.split('T')[0]}T${this.courseDataForm.controls['courseStartTime'].value}` : `${this.courseDataForm.controls['courseStart'].value.toISOString().split('T')[0]}T${this.courseDataForm.controls['courseStartTime'].value}`;
    }

    if(this.courseDataForm.controls['courseEnd'].value !== null && this.courseDataForm.controls['courseEndTime'].value !== null && timeExpression.test(this.courseDataForm.controls['courseEndTime'].value)){
      this.courseData.courseEnd = typeof this.courseDataForm.controls['courseEnd'].value === 'string' ? `${this.courseDataForm.controls['courseEnd'].value.split('T')[0]}T${this.courseDataForm.controls['courseEndTime'].value}` : `${this.courseDataForm.controls['courseEnd'].value.toISOString().split('T')[0]}T${this.courseDataForm.controls['courseEndTime'].value}`;
    }

    if(this.courseDataForm.controls['onlineLectureStart'].value !== null && this.courseDataForm.controls['onlineLectureStartTime'].value !== null && timeExpression.test(this.courseDataForm.controls['onlineLectureStartTime'].value)){            
      this.courseData.onlineLectureStart = typeof this.courseDataForm.controls['onlineLectureStart'].value === 'string' ? `${this.courseDataForm.controls['onlineLectureStart'].value.split('T')[0]}T${this.courseDataForm.controls['onlineLectureStartTime'].value}` : `${this.courseDataForm.controls['onlineLectureStart'].value.toISOString().split('T')[0]}T${this.courseDataForm.controls['onlineLectureStartTime'].value}`;
    }

    if(this.courseDataForm.controls['onlineLectureEnd'].value !== null && this.courseDataForm.controls['onlineLectureEndTime'].value !== null && timeExpression.test(this.courseDataForm.controls['onlineLectureEndTime'].value)){
      this.courseData.onlineLectureEnd = typeof this.courseDataForm.controls['onlineLectureEnd'].value === 'string' ? `${this.courseDataForm.controls['onlineLectureEnd'].value.split('T')[0]}T${this.courseDataForm.controls['onlineLectureEndTime'].value}` : `${this.courseDataForm.controls['onlineLectureEnd'].value.toISOString().split('T')[0]}T${this.courseDataForm.controls['onlineLectureEndTime'].value}`;
    }
    
    for (const controlName in this.courseAddress) {
      if (this.courseDataForm.controls.hasOwnProperty(controlName)) {
        this.courseAddress[controlName] = this.courseDataForm.controls[controlName].value;        
      }
    }   
    
    this.courseLocation.locationName = this.courseDataForm.controls['locationName'].value;
    
    // Clear webAddress if format is not Online or Hybrid (only on save)
    const format = this.courseDataForm.controls['accreditationCourseFormat'].value;
    if (format !== COURSE_FORMAT.online && format !== COURSE_FORMAT.hybrid) {
      this.courseData.webAddress = null;
    }
    
    // Clear online lecture dates if format is not Hybrid (only on save)
    if (format !== COURSE_FORMAT.hybrid) {
      this.courseData.onlineLectureStart = null;
      this.courseData.onlineLectureEnd = null;
    }
  }

  // Get disciplines that are available based on valid accreditations
  getCourseDisciplines(): string[] {
    const allDisciplines = Object.values(COURSE_DISCIPLINES);
    return allDisciplines.filter(discipline => {
      const keywords = this.disciplineKeywords[discipline] || [];
      return keywords.some(keyword => this.hasAccreditationWithKeyword(keyword));
    });
  }

  // Get course types based on selected discipline's valid accreditations
  getCourseTypes(): string[] {
    const selectedDiscipline = this.courseDataForm?.get('accreditationDiscipline')?.value;
    if (!selectedDiscipline) {
      return [];
    }
    
    const disciplineAccreds = this.getAccreditationsForDiscipline(selectedDiscipline);
    const availableTypes: string[] = [];
    
    if (this.hasAccreditationWithKeyword('initial', disciplineAccreds)) {
      availableTypes.push(COURSE_TYPES.initial);
    }
    if (this.hasAccreditationWithKeyword('refresher', disciplineAccreds)) {
      availableTypes.push(COURSE_TYPES.refresher);
    }
    
    return availableTypes;
  }

  // Get course formats based on selected discipline's valid accreditations
  getCourseFormats(): string[] {
    const selectedDiscipline = this.courseDataForm?.get('accreditationDiscipline')?.value;
    if (!selectedDiscipline) {
      return [];
    }
    
    const disciplineAccreds = this.getAccreditationsForDiscipline(selectedDiscipline);
    const availableFormats: string[] = [
      COURSE_FORMAT.inPerson,
      COURSE_FORMAT.videoConferenceLecture,
      COURSE_FORMAT.handsOnExam,
      COURSE_FORMAT.hybrid
    ];
    
    // Only add Online if there's an accreditation with "online" in it
    if (this.hasAccreditationWithKeyword('online', disciplineAccreds)) {
      availableFormats.push(COURSE_FORMAT.online);
    }
    
    return availableFormats;
  }

  // Get course languages based on selected discipline's valid accreditations
  getCourseLanguages(): string[] {
    const selectedDiscipline = this.courseDataForm?.get('accreditationDiscipline')?.value;
    if (!selectedDiscipline) {
      return [];
    }
    
    const disciplineAccreds = this.getAccreditationsForDiscipline(selectedDiscipline);
    const availableLanguages: string[] = [COURSE_LANGUAGE.english];
    
    // Only add Espanol if there's an accreditation with "spanish" in it
    if (this.hasAccreditationWithKeyword('spanish', disciplineAccreds)) {
      availableLanguages.push(COURSE_LANGUAGE.espanol);
    }
    
    return availableLanguages;
  }

  // Check if the selected discipline has valid options
  isDisciplineSelected(): boolean {
    return !!this.courseDataForm?.get('accreditationDiscipline')?.value;
  }

  // Check if website field should be enabled (when format is Online or Hybrid)
  isWebsiteEnabled(): boolean {
    const format = this.courseDataForm?.get('accreditationCourseFormat')?.value;
    return format === COURSE_FORMAT.online || format === COURSE_FORMAT.hybrid;
  }

  // Check if Hybrid format is selected
  isHybridFormat(): boolean {
    const format = this.courseDataForm?.get('accreditationCourseFormat')?.value;
    return format === COURSE_FORMAT.hybrid;
  }

  // Handle discipline change - clear dependent fields if values are invalid
  onDisciplineChange(): void {
    const currentDiscipline = this.courseDataForm?.get('accreditationDiscipline')?.value;
    
    // Get available options for the new discipline
    const availableTypes = this.getCourseTypes();
    const availableLanguages = this.getCourseLanguages();
    const availableFormats = this.getCourseFormats();
    
    // Clear type if current value is not in available types
    const currentType = this.courseDataForm?.get('stage')?.value;
    if (currentType && !availableTypes.includes(currentType)) {
      this.courseDataForm?.get('stage')?.setValue(null);
    }
    
    // Clear language if current value is not in available languages
    const currentLanguage = this.courseDataForm?.get('accreditationCourseLanguage')?.value;
    if (currentLanguage && !availableLanguages.includes(currentLanguage)) {
      this.courseDataForm?.get('accreditationCourseLanguage')?.setValue(null);
    }
    
    // Clear format if current value is not in available formats
    const currentFormat = this.courseDataForm?.get('accreditationCourseFormat')?.value;
    if (currentFormat && !availableFormats.includes(currentFormat)) {
      this.courseDataForm?.get('accreditationCourseFormat')?.setValue(null);
      // Also clear website when format is cleared
      this.courseDataForm?.get('webAddress')?.setValue(null);
      // Also clear online lecture dates when format is cleared
      this.courseDataForm?.get('onlineLectureStart')?.setValue(null);
      this.courseDataForm?.get('onlineLectureStartTime')?.setValue(null);
      this.courseDataForm?.get('onlineLectureEnd')?.setValue(null);
      this.courseDataForm?.get('onlineLectureEndTime')?.setValue(null);
    }
    
    // Update website field based on format
    this.onFormatChange();
  }

  // Handle format change - enable/disable website field and online lecture dates
  onFormatChange(): void {
    const format = this.courseDataForm?.get('accreditationCourseFormat')?.value;
    const webAddressControl = this.courseDataForm?.get('webAddress');
    const onlineLectureStartControl = this.courseDataForm?.get('onlineLectureStart');
    const onlineLectureStartTimeControl = this.courseDataForm?.get('onlineLectureStartTime');
    const onlineLectureEndControl = this.courseDataForm?.get('onlineLectureEnd');
    const onlineLectureEndTimeControl = this.courseDataForm?.get('onlineLectureEndTime');
    
    if (format === COURSE_FORMAT.online || format === COURSE_FORMAT.hybrid) {
      webAddressControl?.enable();
      // Restore saved value if we have one
      if (this.savedWebAddress !== null) {
        webAddressControl?.setValue(this.savedWebAddress);
        this.savedWebAddress = null;
      }
    } else {
      // Save current value before clearing display
      const currentValue = webAddressControl?.value;
      if (currentValue) {
        this.savedWebAddress = currentValue;
      }
      // Clear display but value is saved above. Will be cleared on save if format is not Online/Hybrid
      webAddressControl?.setValue(null);
      webAddressControl?.disable();
    }

    if (format === COURSE_FORMAT.hybrid) {
      onlineLectureStartControl?.enable();
      onlineLectureStartTimeControl?.enable();
      onlineLectureEndControl?.enable();
      onlineLectureEndTimeControl?.enable();
      
      // Pre-populate online lecture start date for new courses if not already set
      if (this.courseData?.id === 0 && !onlineLectureStartControl?.value && this.minimumCourseDate) {
        onlineLectureStartControl?.setValue(this.minimumCourseDate.toISOString());
      }
    } else {
      onlineLectureStartControl?.setValue(null);
      onlineLectureStartTimeControl?.setValue(null);
      onlineLectureEndControl?.setValue(null);
      onlineLectureEndTimeControl?.setValue(null);
      onlineLectureStartControl?.disable();
      onlineLectureStartTimeControl?.disable();
      onlineLectureEndControl?.disable();
      onlineLectureEndTimeControl?.disable();
    }
  }

  filterPeople(event: any) {    
    const value = event.target.value;
    if (!value) {
      this.filteredPersonList = [...this.personList];
      return;
    }
    const filterValue = value.toLowerCase();
    this.filteredPersonList = this.personList.filter(person => person.lastName.toLowerCase().includes(filterValue) || person.firstName.toLowerCase().includes(filterValue) || person.id.toString().toLocaleLowerCase().includes(filterValue));    
  } 

  savePrompt(){
    let isNew = this.courseData.id === 0;
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',      
      data: {message: isNew ? 'Are you sure you want to <strong>Add</strong> this Course?' : 'Are you sure you want to <strong>Save</strong> the Course?',title: isNew ? 'Add the Course' : 'Save the Course'},});
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.saveData();
      }
    })
  }

  saveData() {
    // If editing is disabled (within 7 days of start or post-note received), only allow cancellation changes
    if (this.isEditingDisabled() && this.courseData.id !== 0) {
      // Update only the cancelled status
      this.courseData.cancelled = this.courseDataForm.controls['cancelled'].value;
      
      // Only save the course (for cancellation status), don't save address
      this.atpService.saveATPCourse(this.courseData).subscribe(result=>{
        this.courseData = result;
        this.toastr.success("Course status updated successfully");
      },error=>{
        this.toastr.error("There was an error updating the course: ", error)
      });
      return;
    }
    
    this.updateCourseData();      
    this.atpService.saveATPCourse(this.courseData).subscribe(result=>{
      this.courseData = result;
      
      this.courseLocation.atpCourse = result;

      this.commonService.saveAddress(this.courseAddress).subscribe(result=>{
        this.courseAddress = result;
        this.courseLocation.address = result

        this.atpService.saveATPCourseAddress(this.courseLocation).subscribe(result=>{
          this.courseLocation = result;
          this.courseAddress = this.courseLocation.address;
          this.changeIndicator++;
          this.toastr.success("Success")          
        },error=>{
          this.toastr.error("There was an error saving the ATP course address: ", error)  
        });
      },error=>{
        this.toastr.error("There was an error saving the address: ", error)  
      })      
    },error=>{
      this.toastr.error("There was an error saving the ATP course: ", error)
    });    
  }

  cancelCourse(cancel: boolean){    
    // Don't allow restoring if course editing is disabled (post-note received)
    if (!cancel && this.isEditingDisabled()) {
      this.toastr.warning('Cannot restore a course that has received a post-notification.');
      return;
    }
    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',      
      data: {message: cancel ? 'Are you sure you want to <strong>cancel</strong> the Course?' : 'Are you sure you want to <strong>restore</strong> the Course?',title: cancel ? 'Cancel the Course' : 'Restore the Course'},});
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.courseDataForm.controls['cancelled'].setValue(!this.courseDataForm.controls['cancelled'].value)
        this.saveData();
      }
    })
  }

  uploadRoster(){
    const dialogRef = this.dialog.open(AtpCourseStudentUploadComponent, { 
      data:{course:this.courseData},             
      width: '70%',
      autoFocus: false,});
    dialogRef.afterClosed().subscribe(result => {         
      this.refreshStudentList();
      // Refresh documents tab to show the uploaded roster file
      if (this.courseDocumentComponent) {
        this.courseDocumentComponent.loadDocuments();
      }
    });
  }

  filterTable(): void {    
    this.tableDataFiltered = this.tableData;

    this.tableDataFiltered = this.tableDataFiltered.filter(x => {      
      return x.id?.toString().includes(this.filterTableString) ||      
        x.studentName?.toString().toLowerCase().includes(this.filterTableString.toLowerCase()) ||
        x.courseCertificateNumber?.toString().toLowerCase().includes(this.filterTableString.toLowerCase()) ||
        x.testScore?.toString().toLowerCase().includes(this.filterTableString.toLowerCase())
    });

    this.updateTableDataSource();
  }
  updateTableDataSource(){    
    this.tableDataSource = new MatTableDataSource<any>(this.tableDataFiltered);
    this.tableDataSource.paginator = this.tablePaginator;
    this.tableDataSource.sort = this.tableSort;    
  }

  editTableEntry(row: any): void {
    const dialogRef = this.dialog.open(AtpEditCourseStudentComponent, { 
      data:{student: row, course: this.courseData, status: row?.status || 'Pending'},             
      width: '70%',
      autoFocus: false,});
    dialogRef.afterClosed().subscribe(result => {          
      this.refreshStudentList();     
    });
  }

  displayLimit(text: any, limit: string){
    let newText = text;
    if (typeof text === 'string' && text.length > parseInt(limit)){
      newText = newText.substring(0, limit) + "...";
    }    
    return newText;
  }

  deleteStudent(row: any){    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',      
      data: {message: 'Are you sure you want to <strong>Delete</strong> the student <strong>' + row.studentName + '</strong>?',title: 'Delete the Student'},});
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.atpService.deleteATPPendingCourseStudent(row.id).subscribe(result=>{
          this.refreshStudentList();          
        },error=>{
          this.toastr.error("There was an error deleting the course student: ", error);
        })  
      }
    })
  }

  refreshStudentList(){
    this.changeIndicator++;
    forkJoin({
      processedStudents: this.atpService.getATPCourseStudents(this.courseData.id),
      pendingStudents: this.atpService.getATPPendingCourseStudents(this.courseData.id)
    }).subscribe(results => {
      const processedData = results.processedStudents.map((data) => ({
        ...data,
        studentName: data.student.firstName + ' ' + data.student.lastName,
        status: 'Processed'
      }));
      
      const pendingData = results.pendingStudents.map((data: any) => ({
        ...data,
        studentName: data.firstName + ' ' + data.lastName,
        status: 'Pending'
      }));
      
      this.tableData = [...processedData, ...pendingData];
      this.courseDataForm.controls['students'].setValue(this.tableData.length);
      this.filterTable();
    }, error => {
      this.toastr.error("There was an error getting the course students: ", error);
    });
  }
}

